import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * One-click Bundle & Save add for the cart drawer.
 * Adds a one-time (no selling_plan) variant and lets Horizon morph the cart section.
 */
class GrCartBundleUpsell extends HTMLElement {
  /** @type {boolean} */
  #pending = false;

  connectedCallback() {
    // Delegate so Horizon cart morph can replace the inner button without losing the handler.
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-gr-bundle-add]') : null;
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;
    event.preventDefault();
    void this.#onAdd();
  };

  #onAdd = async () => {
    if (this.#pending) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.#setPending(true);
    this.#setError('');

    const sectionIds = this.#getSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
            },
          ],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const addData = await addResponse.json();

      if (addData.status) {
        const message = addData.description || addData.message || 'Could not add product';
        this.#setError(typeof message === 'string' ? message : 'Could not add product');
        this.dispatchEvent(
          new CartErrorEvent({
            error: addData.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: addData.description,
              errors: addData.errors,
            },
          })
        );

        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'gr-cart-bundle-upsell',
            itemCount: 0,
            sections: addData.sections,
          },
        });
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'gr-cart-bundle-upsell',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: addData.sections,
        },
      });

      this.#announce('Added to cart');
    } catch (error) {
      console.error('[gr-cart-bundle-upsell]', error);
      this.#setError('Network error. Please try again.');
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#setPending(false);
    }
  };

  /** @returns {string[]} */
  #getSectionIds() {
    const ids = new Set();
    const own = this.dataset.sectionId;
    if (own) ids.add(own);

    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    }

    return Array.from(ids);
  }

  async #fetchCart() {
    const response = await fetch(Theme.routes.cart_url, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  /** @param {boolean} pending */
  #setPending(pending) {
    this.#pending = pending;

    const button = this.querySelector('[data-gr-bundle-add]');
    if (button instanceof HTMLButtonElement) {
      button.setAttribute('aria-busy', pending ? 'true' : 'false');
      button.disabled = pending;
    }

    const label = this.querySelector('[data-gr-bundle-label]');
    const busy = this.querySelector('[data-gr-bundle-busy]');
    if (label instanceof HTMLElement) label.hidden = pending;
    if (busy instanceof HTMLElement) busy.hidden = !pending;
  }

  /** @param {string} message */
  #setError(message) {
    const errorEl = this.querySelector('[data-gr-bundle-error]');
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  /** @param {string} message */
  #announce(message) {
    const live = this.querySelector('[data-gr-bundle-live]');
    if (live instanceof HTMLElement) live.textContent = message;
  }
}

if (!customElements.get('gr-cart-bundle-upsell')) {
  customElements.define('gr-cart-bundle-upsell', GrCartBundleUpsell);
}
